<template>
  <div class="about">
    <page-header :title="$t('venue.edit')" :desc="model.name">
      <li class="breadcrumb-item"><router-link :to="{name:'venues.index'}">{{$tc('venue.name', 2)}}</router-link></li>
      <li class="breadcrumb-item">{{model.name}}</li>
    </page-header>
    <page-content>
      <ntm-block :title="$t('venue.links')" v-if="model.uuid">
        <div class="row items-push">
          <div class="col-sm-6 col-md-4 col-xl-2">
            <p class="font-w600 mb-2">{{ $t('table.column.livestreamWidget') }}</p>
            <div class="d-flex align-items-center">
              <button type="button" class="btn btn-sm btn-circle btn-info mr-2" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(broadcastWidgetLink)">
                <i class="fa fa-copy"></i>
              </button>
              <le-qr-code v-if="broadcastWidgetLink" :name="model.name" :link="broadcastWidgetLink"></le-qr-code>
            </div>
          </div>
          <div class="col-sm-6 col-md-4 col-xl-2">
            <p class="font-w600 mb-2">{{ $t('table.column.scoreboardLink') }}</p>
            <div class="d-flex align-items-center">
              <button type="button" class="btn btn-sm btn-circle btn-info mr-2" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(scoreboardLink)">
                <i class="fa fa-copy"></i>
              </button>
              <le-qr-code v-if="scoreboardLink" :name="model.name" :link="scoreboardLink"></le-qr-code>
            </div>
          </div>
          <div class="col-sm-6 col-md-4 col-xl-2">
            <p class="font-w600 mb-2">{{ $t('table.column.playerStatsLink') }}</p>
            <div class="d-flex align-items-center">
              <button type="button" class="btn btn-sm btn-circle btn-info mr-2" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(playerStatsLink)">
                <i class="fa fa-copy"></i>
              </button>
              <le-qr-code v-if="playerStatsLink" :name="model.name" :link="playerStatsLink"></le-qr-code>
            </div>
          </div>
          <div class="col-sm-6 col-md-4 col-xl-2">
            <p class="font-w600 mb-2">{{ $t('table.column.livestreamLink') }}</p>
            <div class="d-flex align-items-center">
              <button type="button" class="btn btn-sm btn-circle btn-info mr-2" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(livestreamLink)">
                <i class="fa fa-copy"></i>
              </button>
              <le-qr-code v-if="livestreamLink" :name="model.name" :link="livestreamLink"></le-qr-code>
            </div>
          </div>
          <div class="col-sm-6 col-md-4 col-xl-2">
            <p class="font-w600 mb-2">{{ $t('table.column.shotClockLink') }}</p>
            <div class="d-flex align-items-center">
              <button type="button" class="btn btn-sm btn-circle btn-info mr-2" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(shotClockLink)">
                <i class="fa fa-copy"></i>
              </button>
              <le-qr-code v-if="shotClockLink" :name="model.name" :link="shotClockLink"></le-qr-code>
            </div>
          </div>
          <div class="col-sm-6 col-md-4 col-xl-2">
            <p class="font-w600 mb-2">{{ $t('table.column.liveGameWebsiteLink') }}</p>
            <div class="d-flex align-items-center">
              <button type="button" class="btn btn-sm btn-circle btn-info mr-2" v-tooltip="$t('tooltip.copyLink')" @click="copyToClipboard(liveGameWebsiteLink)">
                <i class="fa fa-copy"></i>
              </button>
              <le-qr-code v-if="liveGameWebsiteLink" :name="model.name" :link="liveGameWebsiteLink"></le-qr-code>
            </div>
          </div>
        </div>
      </ntm-block>
      <form @submit.prevent="onSubmit()">
        <ntm-block>
          <formly-form :form="form" :model="model" :fields="fields"></formly-form>
          <button class="btn btn-success push-15">{{$t('save')}}</button>
        </ntm-block>
      </form>

      <ntm-block title="Availability">
        <le-week-availability entity-type="Venue" type="AVAILABLE" :entity-uuid="$route.params.uuid"/>
      </ntm-block>
    </page-content>
  </div>
</template>

<script>
import fields from './formDefinition'
import VenueService from '@/services/venue.service'
import router from '@/router'
import { useExternalLinks } from '@/use/useExternalLinks'

export default {
  setup () {
    const { getWebsiteDomain } = useExternalLinks()

    return {
      getWebsiteDomain
    }
  },
  // required properties, field, form, model
  data () {
    return {
      form: {},
      model: {
        address: {}
      },
      isAddressLoaded: false,
      fields: fields
    }
  },
  beforeRouteEnter (to, from, next) {
    VenueService.show(to.params.uuid).then((response) => {
      next(vm => vm.setData(response.data))
    })
  },
  watch: {
    'model.address': {
      handler (val) {
        if (!this.isAddressLoaded) {
          this.isAddressLoaded = true
          return
        }
        if (val && val.timezone) {
          this.$set(this.model, 'timezone', val.timezone)
        }
      },
      immediate: false
    }
  },
  computed: {
    broadcastWidgetLink () {
      return `${this.getWebsiteDomain()}/venues/${this.model.uuid}/broadcast-game`
    },
    scoreboardLink () {
      return `${this.getWebsiteDomain()}/venues/${this.model.uuid}/scoreboard-game`
    },
    playerStatsLink () {
      return `${this.getWebsiteDomain()}/venues/${this.model.uuid}/player-stats`
    },
    livestreamLink () {
      return `${this.getWebsiteDomain()}/venues/${this.model.uuid}/livestream`
    },
    shotClockLink () {
      return `${this.getWebsiteDomain()}/venues/${this.model.uuid}/shot-clock-game`
    },
    liveGameWebsiteLink () {
      return `${this.getWebsiteDomain()}?venue=${this.model.uuid}`
    }
  },
  methods: {
    copyToClipboard (text) {
      this.$copyText(text).then(() => {
        this.$toast.fire({
          title: 'Copied to clipboard',
          icon: 'success'
        })
      })
    },
    setData (data) {
      this.model = data
    },
    onSubmit () {
      VenueService.update(this.model.uuid, this.model).then((response) => {
        router.push({ name: 'venues.index' })
      }).catch((err) => {
        this.form.$errors = err.response.data.errors
      })
    }
  }
}
</script>
